import { useEffect, useRef } from "react";

/**
 * Adds `is-visible` to every `.reveal` descendant once it scrolls into view,
 * with a stagger so siblings cascade rather than popping in together.
 */
export default function useReveal({ stagger = 90 } = {}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const targets = Array.from(root.querySelectorAll(".reveal"));
    if (!targets.length) return undefined;

    if (!("IntersectionObserver" in window)) {
      targets.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry, index) => {
            entry.target.style.animationDelay = `${index * stagger}ms`;
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [stagger]);

  return containerRef;
}
